import { useState, useEffect } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, MapPin, Mail, MessageCircle, Send } from "lucide-react";
import { business } from "@/config/business";
import { getWhatsAppUrl } from "@/lib/whatsapp";
import { trackEvent, trackGoogleAdsConversion, EVENTS } from "@/lib/tracking";
import { scrollToId } from "@/lib/scroll";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCES = [
  { value: "Rumah / Properti", group: "b2c" },
  { value: "Bisnis", group: "b2b" },
  { value: "Perusahaan", group: "b2b" },
  { value: "Commercial / Industrial", group: "commercial" },
];

const GROUP_BY_INTENT = { b2c: "Rumah / Properti", b2b: "Bisnis", commercial: "Commercial / Industrial" };

const BUSINESS_TYPES = ["Kantor", "Hotel", "Restoran/Cafe", "Property", "Gudang", "Sekolah", "Kampus", "Rumah Sakit", "Industri", "Lainnya"];
const NEEDS_B2C = ["Cuci AC", "Service AC", "Pemeriksaan", "Instalasi", "Troubleshooting", "Home Care", "Lainnya"];
const NEEDS_B2B = ["Preventive Maintenance", "Kontrak Maintenance", "Service", "Survey", "Maintenance HVAC", "Lainnya"];
const NEEDS_COMM = ["Maintenance HVAC", "Ducting", "VRF / VRV", "AHU / FCU", "HVAC Assessment", "Lainnya"];

const EMPTY = {
  audience: "Bisnis",
  nama: "", perusahaan: "", jabatan: "", whatsapp: "", email: "", lokasi: "",
  jenis_bisnis: "", jumlah_unit: "", jenis_ac: "", sistem_hvac: "", estimasi_scope: "",
  kebutuhan: "", keterangan: "",
};

const labelCls = "block text-sm font-semibold text-slate-700 mb-1.5";
const inputCls =
  "w-full rounded-lg border border-slate-300 bg-slate-50 px-4 py-3 text-[15px] text-slate-900 outline-none transition-all placeholder:text-slate-400 focus:border-transparent focus:bg-white focus:ring-2 focus:ring-[#25D366]";
const errCls = "mt-1 text-xs font-medium text-red-600";

// Module-level (stable identity) agar input tidak kehilangan fokus saat mengetik.
const LeadField = ({ id, label, placeholder, type = "input", options, inputMode, value, onChange, error }) => (
  <div>
    <label className={labelCls} htmlFor={id}>{label}</label>
    {type === "select" ? (
      <select id={id} data-testid={`select-${id}`} className={inputCls} value={value} onChange={onChange}>
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    ) : type === "textarea" ? (
      <textarea id={id} data-testid={`input-${id}`} rows={4} className={`${inputCls} resize-none`} value={value} onChange={onChange} placeholder={placeholder} />
    ) : (
      <input id={id} data-testid={`input-${id}`} className={inputCls} value={value} onChange={onChange} placeholder={placeholder} inputMode={inputMode} />
    )}
    {error && <p className={errCls} data-testid={`error-${id}`}>{error}</p>}
  </div>
);

export const LeadForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [started, setStarted] = useState(false);

  const group = AUDIENCES.find((a) => a.value === form.audience)?.group || "b2b";
  const needs = group === "b2c" ? NEEDS_B2C : group === "commercial" ? NEEDS_COMM : NEEDS_B2B;

  // Dengarkan intent dari tombol program (SEJUKARA HOME/CARE/HVAC, dll)
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail || {};
      setForm((f) => ({
        ...f,
        audience: GROUP_BY_INTENT[detail.audience] || f.audience,
        kebutuhan: detail.kebutuhan || f.kebutuhan,
      }));
      if (!started) {
        setStarted(true);
        trackEvent(EVENTS.LEAD_FORM_START, { source: "intent" }, { once: true });
      }
    };
    window.addEventListener("sejukara:lead-intent", handler);
    return () => window.removeEventListener("sejukara:lead-intent", handler);
  }, [started]);

  const onFirstInteract = () => {
    if (!started) {
      setStarted(true);
      trackEvent(EVENTS.LEAD_FORM_START, { source: "lead_form" }, { once: true });
    }
  };

  const setField = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const pickAudience = (value) => {
    setForm((f) => ({ ...f, audience: value }));
    onFirstInteract();
  };

  const validate = () => {
    const err = {};
    if (!form.nama.trim()) err.nama = "Nama wajib diisi.";
    const wa = form.whatsapp.replace(/[\s-]/g, "");
    if (!wa) err.whatsapp = "Nomor WhatsApp wajib diisi.";
    else if (!/^\+?\d{8,15}$/.test(wa)) err.whatsapp = "Nomor WhatsApp tidak valid.";
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      err.email = "Email tidak valid.";
    if (group === "b2b" && !form.jenis_bisnis) err.jenis_bisnis = "Pilih jenis bisnis.";
    if (!form.kebutuhan) err.kebutuhan = "Pilih kebutuhan.";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === "submitting") return;
    if (!validate()) return;
    setStatus("submitting");
    try {
      await axios.post(`${API}/leads`, form);
      trackEvent(EVENTS.LEAD_FORM_SUBMIT, { source: "lead_form", audience: group });
      trackEvent(EVENTS.SURVEY_REQUEST, { source: "lead_form" });
      trackGoogleAdsConversion();
      setStatus("success");
      setForm(EMPTY);
    } catch {
      setStatus("error");
    }
  };

  const Field = (props) => (
    <LeadField {...props} value={form[props.id]} onChange={setField(props.id)} error={errors[props.id]} />
  );

  return (
    <section id="lead-form" data-testid="lead-form-section" className="scroll-mt-20 bg-[#F8FAFC] py-20 sm:py-24 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
          {/* Left pitch */}
          <div className="lg:sticky lg:top-28 lg:self-start">
            <Reveal>
              <div className="mb-4 flex items-center gap-3">
                <span className="text-sm font-bold text-[#E0A400]">10</span>
                <span className="text-sm font-bold uppercase tracking-[0.2em] text-slate-500">Kontak</span>
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="text-3xl font-extrabold leading-[1.12] tracking-tight text-slate-900 sm:text-4xl">
                Butuh Layanan AC, HVAC &amp; Maintenance?
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
                Konsultasikan kebutuhan Anda dan dapatkan penawaran yang sesuai —
                untuk rumah, bisnis, maupun fasilitas commercial &amp; industrial.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <a href={getWhatsAppUrl()} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent(EVENTS.WHATSAPP_CLICK, { source: "lead_section" })} data-testid="lead-section-whatsapp-cta" className="mt-8 inline-flex items-center gap-2.5 rounded-lg bg-[#25D366] px-6 py-3.5 text-[15px] font-bold text-white transition-colors hover:bg-[#1eb85a]">
                <MessageCircle className="h-5 w-5" />
                {business.cta.secondary}
              </a>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-10 space-y-4 border-t border-slate-200 pt-8">
                <a href={getWhatsAppUrl()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-slate-700 transition-colors hover:text-slate-900">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#25D366]/10 text-[#25D366]"><MessageCircle className="h-5 w-5" /></span>
                  <span><span className="block text-xs font-semibold uppercase tracking-wide text-slate-400">WhatsApp</span><span className="font-bold">{business.whatsapp.display}</span></span>
                </a>
                <a href={`mailto:${business.email}`} className="flex items-center gap-3 text-slate-700 transition-colors hover:text-slate-900">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-700"><Mail className="h-5 w-5" /></span>
                  <span><span className="block text-xs font-semibold uppercase tracking-wide text-slate-400">Email</span><span className="font-bold">{business.email}</span></span>
                </a>
                <div className="flex items-center gap-3 text-slate-700">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-700"><MapPin className="h-5 w-5" /></span>
                  <span><span className="block text-xs font-semibold uppercase tracking-wide text-slate-400">Area Layanan</span><span className="font-bold">{business.serviceArea}</span></span>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Form card */}
          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_18px_50px_-24px_rgba(15,23,42,0.25)] sm:p-8">
              {status === "success" ? (
                <div data-testid="lead-form-success" className="flex min-h-[420px] flex-col items-center justify-center text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#25D366]/10 text-[#25D366]"><CheckCircle2 className="h-9 w-9" /></span>
                  <h3 className="mt-6 text-2xl font-extrabold text-slate-900">Terima kasih!</h3>
                  <p className="mt-3 max-w-sm text-slate-600">Terima kasih. Tim CV Angkasa Briliant akan menghubungi Anda untuk membahas kebutuhan Anda.</p>
                  <button type="button" onClick={() => setStatus("idle")} data-testid="lead-form-reset" className="mt-8 text-sm font-bold text-[#0F172A] underline underline-offset-4 hover:text-[#25D366]">Kirim permintaan lain</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} onFocus={onFirstInteract} noValidate data-testid="lead-form">
                  <h3 className="text-xl font-bold text-slate-900">Formulir Permintaan Penawaran</h3>

                  {/* Intent selector */}
                  <p className="mt-5 text-sm font-semibold text-slate-700">Untuk kebutuhan siapa?</p>
                  <div className="mt-2 grid grid-cols-2 gap-2" data-testid="audience-selector">
                    {AUDIENCES.map((a) => (
                      <button
                        key={a.value}
                        type="button"
                        onClick={() => pickAudience(a.value)}
                        data-testid={`audience-${a.group}-${a.value.replace(/[^a-z]/gi, "").toLowerCase()}`}
                        aria-pressed={form.audience === a.value}
                        className={`rounded-lg border px-3 py-2.5 text-sm font-bold transition-colors ${
                          form.audience === a.value
                            ? "border-[#0F172A] bg-[#0F172A] text-white"
                            : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"
                        }`}
                      >
                        {a.value}
                      </button>
                    ))}
                  </div>

                  <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {Field({ id: "nama", label: "Nama *", placeholder: "Nama Anda" })}

                    {group !== "b2c" && Field({ id: "perusahaan", label: "Nama Perusahaan", placeholder: "PT / CV / Instansi" })}
                    {group !== "b2c" && Field({ id: "jabatan", label: "Jabatan", placeholder: "mis. Facility Manager" })}

                    {Field({ id: "whatsapp", label: "WhatsApp *", placeholder: "08xxxxxxxxxx", inputMode: "tel" })}
                    {group !== "b2c" && Field({ id: "email", label: "Email", placeholder: "nama@perusahaan.com", inputMode: "email" })}

                    {Field({ id: "lokasi", label: group === "commercial" ? "Lokasi Proyek" : "Lokasi", placeholder: "Kota / area" })}

                    {group === "b2b" && Field({ id: "jenis_bisnis", label: "Jenis Bisnis *", type: "select", placeholder: "Pilih jenis bisnis", options: BUSINESS_TYPES })}
                    {group === "commercial" && Field({ id: "sistem_hvac", label: "Sistem HVAC", placeholder: "mis. VRF, AHU, Ducting" })}

                    {group !== "commercial" && Field({ id: "jumlah_unit", label: "Jumlah Unit AC", placeholder: "mis. 12 unit", inputMode: "numeric" })}
                    {group !== "commercial" && Field({ id: "jenis_ac", label: "Jenis AC", placeholder: "mis. Split, Cassette, Standing" })}

                    {Field({ id: "kebutuhan", label: group === "b2b" ? "Kebutuhan Maintenance *" : "Kebutuhan *", type: "select", placeholder: "Pilih kebutuhan", options: needs })}

                    {group === "commercial" && Field({ id: "estimasi_scope", label: "Estimasi Scope", placeholder: "mis. 1 gedung, 3 lantai" })}

                    <div className="sm:col-span-2">
                      {Field({ id: "keterangan", label: "Keterangan", type: "textarea", placeholder: "Ceritakan kebutuhan Anda..." })}
                    </div>
                  </div>

                  {status === "error" && (
                    <p data-testid="lead-form-error" className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                      Maaf, terjadi kesalahan saat mengirim. Silakan coba lagi atau hubungi kami via WhatsApp.
                    </p>
                  )}

                  <button type="submit" disabled={status === "submitting"} data-testid="lead-form-submit" className="mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-lg bg-[#25D366] px-6 py-4 text-base font-bold text-white transition-colors hover:bg-[#1eb85a] disabled:cursor-not-allowed disabled:opacity-70">
                    {status === "submitting" ? (<><Loader2 className="h-5 w-5 animate-spin" /> Mengirim...</>) : (<><Send className="h-5 w-5" /> {business.cta.form}</>)}
                  </button>
                  <p className="mt-3 text-center text-xs text-slate-400">Data Anda hanya digunakan untuk menindaklanjuti permintaan Anda.</p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default LeadForm;
